import React from 'react';
import { POLICYTYPE } from '~/modules/common/enums';
import RoleFacet, { primaryRoleFacet } from '../SearchBox/Facets/RoleFacet';
import LocationFacet from '../SearchBox/Facets/LocationFacet';
import DivisionFacet from '../SearchBox/Facets/DivisionFacet';
import ProgramFacet from '../SearchBox/Facets/ProgramFacet';
import ClientFacet from '../SearchBox/Facets/ClientFacet';
import ProjectFacet from '../SearchBox/Facets/ProjectFacet';
import ServiceCenterFacet from '../SearchBox/Facets/ServiceCenterFacet';
import SkillFacet from '../SearchBox/Facets/SkillFacet';
import CostCenterFacet from '../SearchBox/Facets/CostCenterFacet';
import EmployeeTypeFacet from '../SearchBox/Facets/EmployeeTypeFacet';
import DepartmentFacet from '../SearchBox/Facets/DepartmentFacet';
import TagFacet from '../SearchBox/Facets/TagFacet';
import ResourcePoolFacet from '../SearchBox/Facets/ResourcePoolFacet';
import {
  ResourceFacet,
  ProjectResourceFacet
} from '../SearchBox/Facets/UserFacet';

const withPolicyBasedGroupFacets = policy => BaseComponent => props => (
  <BaseComponent {...props} policy={policy} />
);

const hasResourceManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(
    'urn:replicon:user-data-access-level:resource-manager'
  );

const hasResourcePoolManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(
    'urn:replicon:user-data-access-level:resource-pool-manager'
  );

const hasProjectManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(
    'urn:replicon:user-data-access-level:project-manager'
  );

const applyPolicyToGroupFacet = (policy, facet) => ({
  ...facet,
  DetailsControl: withPolicyBasedGroupFacets(policy)(facet.DetailsControl)
});

const hasViewPermission = ({
  permissions,
  actionUri,
  includeProjectManagerDataAccessScope = false
}) =>
  permissions.some(
    p =>
      p.permissionActionUri === actionUri &&
      (hasResourceManagerDataAccessLevel(p) ||
        hasResourcePoolManagerDataAccessLevel(p) ||
        (includeProjectManagerDataAccessScope &&
          hasProjectManagerDataAccessLevel(p)))
  );

const getFacets = ({
  groupSettings,
  permissions,
  legacyDepartmentDisabled,
  legacyEmployeeTypeDisabled,
  filterFacetOptions,
  facetPolicy = POLICYTYPE.RESOURCE_MANAGEMENT
}) => {
  const includeProjectManagerDataAccessScope =
    facetPolicy === POLICYTYPE.PROJECT_MANAGEMENT;

  return [
    ...[
      includeProjectManagerDataAccessScope
        ? ProjectResourceFacet
        : ResourceFacet
    ],
    ...(filterFacetOptions.role &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-project-role'
    })
      ? [primaryRoleFacet, RoleFacet]
      : []),
    ...(filterFacetOptions.project ? [ProjectFacet] : []),
    ...(filterFacetOptions.client ? [ClientFacet] : []),
    ...(filterFacetOptions.program ? [ProgramFacet] : []),
    ...(filterFacetOptions.skill &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-skill-assignments'
    })
      ? [SkillFacet]
      : []),
    ...(filterFacetOptions.location &&
    groupSettings.location?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-location',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, LocationFacet)]
      : []),
    ...(filterFacetOptions.division &&
    groupSettings.division?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-division',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, DivisionFacet)]
      : []),
    ...(filterFacetOptions.serviceCenter &&
    groupSettings.serviceCenter?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-service-center',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, ServiceCenterFacet)]
      : []),
    ...(filterFacetOptions.costCenter &&
    groupSettings.costCenter?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-cost-center',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, CostCenterFacet)]
      : []),
    ...(filterFacetOptions.department &&
    groupSettings.department?.isEnabled &&
    legacyDepartmentDisabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-department',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, DepartmentFacet)]
      : []),
    ...(filterFacetOptions.employeeType &&
    groupSettings.employeeType?.isEnabled &&
    legacyEmployeeTypeDisabled &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-employee-type',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, EmployeeTypeFacet)]
      : []),
    ...(filterFacetOptions.resourcePool &&
    permissions.some(
      p =>
        p.permissionActionUri === 'urn:replicon:user-action:view-resource-pool'
    )
      ? [ResourcePoolFacet]
      : []),
    ...(filterFacetOptions.tags &&
    hasViewPermission({
      permissions,
      actionUri: 'urn:replicon:user-action:view-custom-fields',
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, TagFacet)]
      : [])
  ];
};

export default getFacets;
